import { Video, Image as ImageIcon, Activity, CircleSlash, Loader2, Clock } from "lucide-react";
import clsx from "clsx";
import type { MediaDescriptor, MediaType, MediaStatus } from "../types/api";
import { fmtTime } from "../lib/format";

const TYPE_META: Record<MediaType, { icon: typeof Video; label: string; color: string }> = {
  video: { icon: Video, label: "Video", color: "#22D3EE" },
  image: { icon: ImageIcon, label: "Snapshot", color: "#3B82F6" },
  telemetry: { icon: Activity, label: "Telemetry", color: "#64748B" },
  none: { icon: CircleSlash, label: "No media", color: "#475569" },
};

const STATUS_LABEL: Record<MediaStatus, string> = {
  available: "Available",
  processing: "Processing",
  unavailable: "Unavailable",
  expired: "Expired / purged",
};

function tooltipText(m: MediaDescriptor): string {
  const bits = [`${TYPE_META[m.type].label} · ${STATUS_LABEL[m.status]}`];
  if (m.duration_s) bits.push(`${m.duration_s}s (pre/post)`);
  if (m.resolution) bits.push(m.resolution);
  bits.push(`captured ${fmtTime(m.captured_at)}`);
  bits.push(`retention: ${m.retention}`);
  return bits.join(" · ");
}

/** Compact at-a-glance media badge for feed rows / table cells. */
export function MediaBadge({ media }: { media?: MediaDescriptor | null }) {
  if (!media) return null;
  const meta = TYPE_META[media.type] ?? TYPE_META.none;
  const Icon = media.status === "processing" ? Loader2 : meta.icon;
  const dim = media.status === "unavailable" || media.status === "expired";
  const color = media.status === "processing" ? "#F59E0B" : dim ? "#64748B" : meta.color;
  return (
    <span
      title={tooltipText(media)}
      className={clsx(
        "mono inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px]",
        dim && "line-through opacity-60"
      )}
      style={{ color, background: `${color}14`, border: `1px solid ${color}33` }}
    >
      <Icon size={11} className={media.status === "processing" ? "animate-spin" : ""} />
      {meta.label}
    </span>
  );
}

/** Prominent media-availability panel for the detail slide-over. */
export function MediaPanel({
  media,
  redZone,
  advisoryZone,
  modelVersion,
  risk,
  overlay,
}: {
  media: MediaDescriptor;
  redZone?: number;
  advisoryZone?: number;
  modelVersion?: string;
  risk?: number;
  overlay?: React.ReactNode;
}) {
  if (!media) return null;
  const meta = TYPE_META[media.type] ?? TYPE_META.none;
  const Icon = meta.icon;
  const playable = media.status === "available" && (media.video_url || media.image_url);

  return (
    <div>
      {/* status header */}
      <div className="mb-2 flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2">
        <div className="flex items-center gap-2">
          <Icon size={16} style={{ color: meta.color }} />
          <span className="text-sm font-medium">{meta.label}</span>
          <StatusChip status={media.status} />
        </div>
        <div className="mono text-[10px] text-slatey">
          {media.duration_s ? `${media.duration_s}s · ` : ""}
          {media.resolution ?? "—"}
        </div>
      </div>

      {/* media or graceful fallback — never a blank/broken player */}
      <div className="relative aspect-video overflow-hidden rounded-lg border border-white/10 bg-black">
        {playable && media.video_url ? (
          <video
            key={media.video_url}
            src={media.video_url}
            className="h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            controls
          />
        ) : playable && media.image_url ? (
          <img src={media.image_url} alt="snapshot" className="h-full w-full object-cover" />
        ) : (
          <MediaFallback media={media} />
        )}
        {playable && media.video_url && overlay}
      </div>

      {/* metadata */}
      <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-slatey">
        <Meta label="CAPTURED" value={fmtTime(media.captured_at)} />
        <Meta label="RETENTION" value={media.retention} icon={<Clock size={11} />} />
      </div>
    </div>
  );
}

function StatusChip({ status }: { status: MediaStatus }) {
  const map: Record<MediaStatus, { color: string; pulse?: boolean }> = {
    available: { color: "#2DD4BF" },
    processing: { color: "#F59E0B", pulse: true },
    unavailable: { color: "#64748B" },
    expired: { color: "#64748B" },
  };
  const s = map[status];
  return (
    <span
      className="mono inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-wider"
      style={{ color: s.color, background: `${s.color}1a`, border: `1px solid ${s.color}40` }}
    >
      <span className={clsx("h-1.5 w-1.5 rounded-full", s.pulse && "animate-pulseDot")} style={{ background: s.color }} />
      {STATUS_LABEL[status]}
    </span>
  );
}

function MediaFallback({ media }: { media: MediaDescriptor }) {
  const meta = TYPE_META[media.type];
  const Icon = media.status === "processing" ? Loader2 : meta.icon;
  const msg =
    media.status === "processing"
      ? "Media is processing — check back shortly"
      : media.status === "expired"
        ? "Clip expired / purged per retention policy"
        : media.status === "unavailable"
          ? "Media unavailable for this exception"
          : media.type === "telemetry"
            ? "Telemetry-only event — no camera media captured"
            : "No media attached";
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 text-slatey">
      <Icon size={28} className={media.status === "processing" ? "animate-spin text-accent" : ""} />
      <span className="text-xs">{msg}</span>
      {media.type === "telemetry" && (
        <span className="mono text-[10px] text-slate-600">lat/lng · speed · dead-man · audio</span>
      )}
    </div>
  );
}

function Meta({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="rounded border border-white/5 bg-white/5 px-2 py-1">
      <div className="section-tag">{label}</div>
      <div className="mt-0.5 flex items-center gap-1 text-ink">{icon}{value}</div>
    </div>
  );
}
